import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { QueryState } from '@/components/QueryState'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import {
  createEntry,
  formatMoney,
  listAccounts,
  today,
  type JournalEntryInput,
} from '@/features/accounting/api'
import { blankLine, lineProblem, toMinor, type DraftLine } from '@/features/accounting/journalDraft'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * Post a manual journal entry.
 *
 * Everything automatic -- invoices, payments, POS, stock -- posts through posting rules, so
 * this screen is for the rest: accruals, opening balances, a correction that is not simply
 * the reverse of something already posted. It submits only when the entry balances and
 * every line has exactly one side, which is what the API enforces too; checking here first
 * means the person typing sees the difference while they can still fix it, not as a 422.
 *
 * There is no draft state. A posted entry is immutable, so the confirmation is the button
 * itself, and the way back from a mistake is a reversal from the entry's own page.
 */
export function JournalEntryNew() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [entryDate, setEntryDate] = useState(today())
  const [description, setDescription] = useState('')
  const [memo, setMemo] = useState('')
  const [lines, setLines] = useState<DraftLine[]>(() => [blankLine(), blankLine()])
  const [touched, setTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    data: accountsPage,
    isLoading,
    error: accountsError,
  } = useQuery({
    queryKey: ['accounts', 'postable'],
    queryFn: () => listAccounts({ include_inactive: false }),
    retry: false,
  })

  // Header accounts only group their children; the API refuses a line against one.
  const accounts = (accountsPage?.data ?? [])
    .filter((account) => account.is_postable && account.is_active)
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))

  const post = useMutation({
    mutationFn: (input: JournalEntryInput) => createEntry(input),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['journal-entries'] })
      void queryClient.invalidateQueries({ queryKey: ['trial-balance'] })
      toast.success(`Posted ${created.entry_number}.`)
      navigate(`/accounting/journal/${created.id}`)
    },
    onError: (mutationError: unknown) => {
      setError(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not reach the server. Nothing was posted.',
      )
    },
  })

  const update = (key: number, patch: Partial<DraftLine>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)))

  const remove = (key: number) => setLines((current) => current.filter((line) => line.key !== key))

  const totalDebit = lines.reduce((sum, line) => sum + (toMinor(line.debit) ?? 0), 0)
  const totalCredit = lines.reduce((sum, line) => sum + (toMinor(line.credit) ?? 0), 0)
  const difference = totalDebit - totalCredit
  const problems = lines.map(lineProblem)

  const formProblem = !description.trim()
    ? 'Describe the entry.'
    : !entryDate
      ? 'Pick the date it posts on.'
      : problems.some(Boolean)
        ? 'Fix the lines marked below.'
        : difference !== 0
          ? 'Debits and credits must be equal.'
          : null

  const submit = () => {
    setTouched(true)
    setError(null)
    if (formProblem) return
    post.mutate({
      entry_date: entryDate,
      description: description.trim(),
      memo: memo.trim() || null,
      lines: lines.map((line) => ({
        account_id: line.account_id,
        debit: line.debit.trim() || '0',
        credit: line.credit.trim() || '0',
        description: line.description.trim() || null,
      })),
    })
  }

  return (
    <div className="space-y-4">
      <Link
        to="/accounting/journal"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Journal
      </Link>

      <header>
        <h1 className="text-xl font-semibold tracking-tight">New journal entry</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Posts straight to the ledger. It cannot be edited afterwards — only reversed.
        </p>
      </header>

      {error && <Alert tone="error">{error}</Alert>}

      <QueryState isLoading={isLoading} error={accountsError}>
        <Card>
          <CardHeader title="Entry" />
          <div className="grid gap-4 p-5 tablet:grid-cols-[12rem_1fr]">
            <Field
              label="Date"
              hint="Must fall in an open period."
              error={touched && !entryDate ? 'Pick the date it posts on.' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={entryDate}
                  onChange={(event) => setEntryDate(event.target.value)}
                />
              )}
            </Field>
            <Field
              label="Description"
              error={touched && !description.trim() ? 'Describe the entry.' : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  maxLength={255}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Accrued rent for September"
                />
              )}
            </Field>
            <div className="tablet:col-span-2">
              <Field label="Memo" hint="Optional. Shown on the entry, not on its lines.">
                {(props) => (
                  <Input
                    {...props}
                    value={memo}
                    onChange={(event) => setMemo(event.target.value)}
                  />
                )}
              </Field>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Lines"
            description="Each line carries a debit or a credit. At least two lines, and the totals must match."
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Lines of the new entry</caption>
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-2 font-medium">Account</th>
                  <th className="px-5 py-2 font-medium">Description</th>
                  <th className="w-36 px-5 py-2 text-right font-medium">Debit</th>
                  <th className="w-36 px-5 py-2 text-right font-medium">Credit</th>
                  <th className="w-12 px-2 py-2">
                    <span className="sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {lines.map((line, index) => {
                  const problem = touched ? problems[index] : null
                  return (
                    <tr key={line.key} className="align-top">
                      <td className="min-w-56 px-5 py-2">
                        <select
                          aria-label={`Line ${index + 1} account`}
                          className={SELECT_CLASS}
                          value={line.account_id}
                          onChange={(event) => update(line.key, { account_id: event.target.value })}
                        >
                          <option value="">Choose an account…</option>
                          {accounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.code} · {account.name}
                            </option>
                          ))}
                        </select>
                        {problem && (
                          <p role="alert" className="mt-1 text-xs text-danger">
                            {problem}
                          </p>
                        )}
                      </td>
                      <td className="min-w-48 px-5 py-2">
                        <Input
                          aria-label={`Line ${index + 1} description`}
                          value={line.description}
                          onChange={(event) =>
                            update(line.key, { description: event.target.value })
                          }
                        />
                      </td>
                      <td className="px-5 py-2">
                        <Input
                          aria-label={`Line ${index + 1} debit`}
                          inputMode="decimal"
                          className="text-right tabular-nums"
                          value={line.debit}
                          // A line is one side or the other, so typing a debit clears the credit.
                          onChange={(event) =>
                            update(line.key, {
                              debit: event.target.value,
                              ...(event.target.value ? { credit: '' } : {}),
                            })
                          }
                        />
                      </td>
                      <td className="px-5 py-2">
                        <Input
                          aria-label={`Line ${index + 1} credit`}
                          inputMode="decimal"
                          className="text-right tabular-nums"
                          value={line.credit}
                          onChange={(event) =>
                            update(line.key, {
                              credit: event.target.value,
                              ...(event.target.value ? { debit: '' } : {}),
                            })
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove line ${index + 1}`}
                          disabled={lines.length <= 2}
                          onClick={() => remove(line.key)}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot className="border-t border-slate-200 font-medium dark:border-slate-800">
                <tr>
                  <td className="px-5 py-2" colSpan={2}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Add line
                    </Button>
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums" data-testid="total-debit">
                    {formatMoney(totalDebit / 100)}
                  </td>
                  <td className="px-5 py-2 text-right tabular-nums" data-testid="total-credit">
                    {formatMoney(totalCredit / 100)}
                  </td>
                  <td />
                </tr>
                {difference !== 0 && (
                  <tr>
                    <td className="px-5 pb-2 text-right text-danger" colSpan={4}>
                      Out of balance by {formatMoney(Math.abs(difference) / 100)}
                    </td>
                    <td />
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        </Card>

        <div className="flex items-center justify-end gap-3">
          {touched && formProblem && <p className="text-sm text-danger">{formProblem}</p>}
          <Button variant="secondary" onClick={() => navigate('/accounting/journal')}>
            Cancel
          </Button>
          <Button loading={post.isPending} onClick={submit}>
            Post entry
          </Button>
        </div>
      </QueryState>
    </div>
  )
}
